import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiPlus, FiShoppingCart, FiInfo, FiArrowRight, FiPackage } = FiIcons;

const ScanActionModal = ({ isOpen, onClose, product, onAction }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Product Found</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">This item is already in your inventory.</p>
              </div>
              <button 
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <SafeIcon icon={FiX} className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-4 mb-6">
                <div className="h-16 w-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                  {product.image ? (
                    <img 
                      src={product.image} 
                      alt={product.name} 
                      className="h-full w-full object-cover"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div className={`h-full w-full items-center justify-center bg-gray-100 dark:bg-gray-700 ${product.image ? 'hidden' : 'flex'}`}>
                    <SafeIcon icon={FiPackage} className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                  </div>
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 dark:text-white truncate">{product.name}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{product.manufacturer} {product.model}</p>
                  <div className="flex items-center mt-1 space-x-2">
                    <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">Qty: {product.quantity}</span>
                    <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 px-2 py-0.5 rounded">${product.localPrice?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => onAction('add')}
                  className="w-full flex items-center justify-between p-4 border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/40 rounded-lg transition-colors group text-left"
                >
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-green-200 dark:bg-green-800 rounded-full flex items-center justify-center mr-3">
                      <SafeIcon icon={FiPlus} className="h-5 w-5 text-green-700 dark:text-green-200" />
                    </div>
                    <div>
                      <p className="font-medium text-green-900 dark:text-green-100">Add Inventory</p>
                      <p className="text-xs text-green-700 dark:text-green-300">Add 1 unit to stock</p>
                    </div>
                  </div>
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 text-green-400 dark:text-green-500 group-hover:text-green-600 dark:group-hover:text-green-300" />
                </button>

                <button
                  onClick={() => onAction('sale')}
                  className="w-full flex items-center justify-between p-4 border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors group text-left"
                >
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-blue-200 dark:bg-blue-800 rounded-full flex items-center justify-center mr-3">
                      <SafeIcon icon={FiShoppingCart} className="h-4 w-4 text-blue-700 dark:text-blue-200" />
                    </div>
                    <div>
                      <p className="font-medium text-blue-900 dark:text-blue-100">Mark as Sold</p>
                      <p className="text-xs text-blue-700 dark:text-blue-300">Remove 1 unit (Sale)</p>
                    </div>
                  </div>
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 text-blue-400 dark:text-blue-500 group-hover:text-blue-600 dark:group-hover:text-blue-300" />
                </button>

                <button
                  onClick={() => onAction('view')}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors group text-left"
                >
                  <div className="flex items-center">
                    <div className="h-8 w-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center mr-3">
                      <SafeIcon icon={FiInfo} className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">View Details</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Edit or view full history</p>
                    </div>
                  </div>
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ScanActionModal;